import React from 'react'
// libs
import { useSelector } from 'react-redux'
// hooks
import { useApolloClient } from 'hooks/useApolloClient'
// graphql
import { PLANS_DETAILS_QUERY } from 'graphql/plan/queries'
import { PLAN_TYPES_DETAILS_QUERY } from 'graphql/planType/queries'
// types
import { IObjectEntity, IPlanType } from 'types/graphql'
// store
import { objectEntitiesSelector } from 'store/reducers/objectEntities'
// utils
import get from 'lodash/get'
import pipe from 'lodash/fp/pipe'
import find from 'lodash/fp/find'
import isEmpty from 'lodash/isEmpty'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'

export const useDefaultObjectSecurity = ({
  objectApiName,
  planId,
  planType,
  planTypeId
}: {
  objectApiName: string
  planId?: string
  planType?: IPlanType
  planTypeId?: string
}) => {
  const { client } = useApolloClient()

  const [planTypeObjectSecurity, setPlanTypeObjectSecurity] = React.useState(planType)

  const objectEntities = useSelector(objectEntitiesSelector)

  React.useMemo(async () => {
    try {
      if (planTypeId) {
        const value = get(
          await client.query({ query: PLAN_TYPES_DETAILS_QUERY, variables: { filter: { ids: [planTypeId] } } }),
          'data.planTypes.data[0]'
        )
        setPlanTypeObjectSecurity(value)
      }

      if (planId) {
        const value = get(
          await client.query({ query: PLANS_DETAILS_QUERY, variables: { filter: { id: planId } } }),
          'data.plans.data[0].planType'
        )
        setPlanTypeObjectSecurity(value)
      }

      if (planType) {
        setPlanTypeObjectSecurity(planType)
      }

      if (!planTypeId && !planId && !planType) {
        setPlanTypeObjectSecurity(null)
      }
    } catch (e) {
      console.log('[useDefaultObjectSecurity]:', parseGraphQLErrorMessages(e))
    }
  }, [planId, planTypeId, planType])

  if (isEmpty(objectEntities)) {
    return
  }

  const key = objectApiName.slice(0, -1)

  if (planTypeObjectSecurity) {
    return {
      ownerObjectSecurityId: planTypeObjectSecurity[`${key}OwnerObjectSecurityId`],
      memberObjectSecurityId: planTypeObjectSecurity[`${key}MemberObjectSecurityId`]
    }
  }

  return pipe<IObjectEntity[], IObjectEntity, IObjectEntity>(
    find(({ apiName }) => apiName === objectApiName),
    item => ({
      ownerObjectSecurityId: get(item, 'ownerObjectSecurityId'),
      memberObjectSecurityId: get(item, 'memberObjectSecurityId')
    })
    // @ts-ignore
  )(objectEntities)
}
